/**
 * Shared session management utility
 * Handles session expiration detection and user logout across the application
 */

// Global flag to prevent duplicate session expiration dialogs
let isSessionExpiredDialogShown = false;

// Timeout to reset the flag after dialog is dismissed
let resetFlagTimeout = null;

/**
 * Check if the URL is from our own API (not third-party)
 * @param {string} url - The request URL
 * @returns {boolean} - True if it's our own API
 */
const isOwnApiRequest = (url) => {
  if (!url) return true; // Assume it's our API if no URL provided
  
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
  const multiLevelBaseUrl = process.env.REACT_APP_MULTI_LEVEL_BASE_URL || '';
  
  // Exclude third-party URLs
  const thirdPartyDomains = [
    'ugiftme.com',
    'stripe.com',
    'paystack.com',
    'beepbeep',
    // Add other third-party domains as needed
  ];
  
  // If URL contains third-party domain, it's not our API
  if (thirdPartyDomains.some(domain => url.includes(domain))) {
    return false;
  }
  
  // Check if the URL starts with our API base URLs
  return url.includes(apiBaseUrl) || 
         url.includes(multiLevelBaseUrl) ||
         url.includes('/graphql');
};

/**
 * Check if the error indicates a genuine session expiration (not just permission/access denial)
 * @param {Object} error - The error object
 * @param {Object} responseData - The response data from the API
 * @returns {boolean} - True if it's a session expiration
 */
const isSessionExpiration = (error, responseData = null) => {
  // Check for specific error codes or messages that indicate session expiration
  const errorMessage = (error?.message || '')?.toLowerCase();
  const errorCode = error?.extensions?.code || '';
  const responseMessage = (responseData?.message || responseData?.error || '')?.toLowerCase();
  
  // Specific session/token expiration indicators
  const sessionExpirationKeywords = [
    'session expired',
    'token expired',
    'token has expired',
    'jwt expired',
    'authentication token expired',
    'authentication token is invalid',
    'invalid authentication token',
    'token is invalid',
    'token invalid',
    'must sign in',
    'must be signed in',
    'please sign in',
    'please log in again',
    'login again',
    'session timeout',
    'authentication timeout',
    'token not found',
    'no token provided'
  ];
  
  // Keywords that indicate permission/access issues (NOT session expiration)
  const permissionKeywords = [
    'permission denied',
    'access denied',
    'insufficient permissions',
    'not authorized to access',
    'forbidden',
    'you do not have',
    'unauthorized access to',
    'api key',
    'invalid credentials',
    'incorrect credentials'
  ];
  
  // Check if it's a permission issue (should NOT trigger session expiration)
  const combinedMessage = `${errorMessage} ${responseMessage}`;
  if (permissionKeywords.some(keyword => combinedMessage.includes(keyword))) {
    console.log('401 is a permission issue, not session expiration');
    return false;
  }
  
  // Check for UNAUTHENTICATED code from GraphQL
  if (errorCode === 'UNAUTHENTICATED') {
    // Even with UNAUTHENTICATED, check if it's permission-related
    if (permissionKeywords.some(keyword => combinedMessage.includes(keyword))) {
      return false;
    }
    return true;
  }
  
  // Check error message for session expiration terms
  const hasSessionKeyword = sessionExpirationKeywords.some(keyword => 
    combinedMessage.includes(keyword)
  );
  
  return hasSessionKeyword;
};

/**
 * Check if we're on a login/signup page
 * @returns {boolean} - True if on login page
 */
const isOnLoginPage = () => {
  const pathname = window.location.pathname;
  return pathname === '/' || 
         pathname === '/signin' || 
         pathname === '/signup' ||
         pathname.startsWith('/signin') ||
         pathname.startsWith('/signup');
};

/**
 * Reset the session expiration flag after a delay
 */
const scheduleReset = () => {
  if (resetFlagTimeout) {
    clearTimeout(resetFlagTimeout);
  }
  
  // Reset the flag after 5 seconds to allow for cleanup
  resetFlagTimeout = setTimeout(() => {
    isSessionExpiredDialogShown = false;
    resetFlagTimeout = null;
  }, 5000);
};

/**
 * Clear all authentication data
 */
const clearAuthData = () => {
  localStorage.removeItem("userDetails");
  localStorage.removeItem("organisation_Details");
  localStorage.removeItem("loystar-access-token");
  localStorage.removeItem("multi-level-token");
  localStorage.removeItem("mtier-token");
};

/**
 * Handle session expiration and redirect to login
 * @param {Object} options - Options for handling session expiration
 * @param {string} options.url - The request URL (optional)
 * @param {Object} options.error - The error object (optional)
 * @param {Object} options.responseData - The response data from API (optional)
 * @param {boolean} options.forceShow - Force showing the dialog even if already shown (optional)
 */
export const handleSessionExpiration = ({ url = null, error = null, responseData = null, forceShow = false } = {}) => {
  // Don't show dialog if we're already on the login page
  if (isOnLoginPage()) {
    console.log('Already on login page, skipping session expiration dialog');
    return;
  }
  
  // Check if it's our own API request (not third-party)
  if (url && !isOwnApiRequest(url)) {
    console.log('401 from third-party API, not triggering session expiration:', url);
    return;
  }
  
  // Check if the error truly indicates session expiration (not permission/access issues)
  if (!isSessionExpiration(error, responseData)) {
    console.log('401 but not a session expiration - likely permission/access issue:', {
      url,
      error: error?.message,
      responseData
    });
    return;
  }
  
  // Prevent duplicate dialogs unless forced
  if (isSessionExpiredDialogShown && !forceShow) {
    console.log('Session expiration dialog already shown, preventing duplicate');
    return;
  }
  
  console.log('Triggering session expiration dialog for:', { url, error: error?.message });
  isSessionExpiredDialogShown = true;
  
  // Import notification dynamically to avoid circular dependencies
  import('antd').then(({ Modal }) => {
    Modal.error({
      title: "Session Expired",
      content: "Your session has expired. Please log in again.",
      onOk: () => {
        clearAuthData();
        scheduleReset();
        window.location.href = '/signin';
      },
      onCancel: () => {
        clearAuthData();
        scheduleReset();
        window.location.href = '/signin';
      }
    });
  });
};

/**
 * Reset the session expiration flag (useful for testing or forced resets)
 */
export const resetSessionFlag = () => {
  isSessionExpiredDialogShown = false;
  if (resetFlagTimeout) {
    clearTimeout(resetFlagTimeout);
    resetFlagTimeout = null;
  }
};

/**
 * Check if session expiration dialog is currently shown
 * @returns {boolean}
 */
export const isDialogShown = () => {
  return isSessionExpiredDialogShown;
};

export default {
  handleSessionExpiration,
  resetSessionFlag,
  isDialogShown,
  clearAuthData
};
